import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useAxios from "../../../customHooks/useAxios";
import ToastAlertView from "../../common/alert/ToastAlertView";
import ModalCloseButton from "../../common/modal/ModalCloseButton";
import DataCompareService from "../../../services/DataCompareService";
import "../EmailReceiverManagementStyle.css";

const modalStyle = {
    overlay: {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1000,
    },
    content: {
        color: "black",
        width: "500px",
        height: "400px",
        margin: "auto",
        borderRadius: "8px",
    },
};

const requestBodyInit = {
    emailType: "ORDER",
    emailAddress: "",
};

const isValidMapInit = {
    emailType: true,
    emailAddress: false,
};

const AddEmailReceiverModal = ({ isOpen, setIsOpen, emailType, onRefresh }) => {
    const { axiosCall } = useAxios();
    const { t } = useTranslation();

    const [requestBody, setRequestBody] = useState(requestBodyInit);
    const [isValidMap, setIsValidMap] = useState(isValidMapInit);
    const [isValid, setIsValid] = useState(false);

    // Email validation regex
    const emailRegex = /^[0-9a-zA-Z-_]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]+$/;

    const updateRequestBody = (key, value) => {
        setRequestBody((prevObj) => ({ ...prevObj, [key]: value }));
    };

    const updateIsValidMap = (key, value) => {
        setIsValidMap((prevObj) => ({ ...prevObj, [key]: value }));
    };

    const handleEmailAddressChange = (e) => {
        updateRequestBody("emailAddress", e.target.value);
    };

    const validateEmailAddress = () => {
        const isValidEmail = DataCompareService.regexValidator(
            requestBody["emailAddress"],
            emailRegex.source
        );
        updateIsValidMap("emailAddress", isValidEmail && requestBody["emailAddress"].length >= 6);
    };

    const resetForm = () => {
        setRequestBody(requestBodyInit);
        setIsValidMap(isValidMapInit);
    };

    const handleSubmit = async () => {
        if (!isValid) return;

        try {
            const res = await axiosCall(
                "POST",
                "/emailReceiver",
                [],
                requestBody,
                null,
                {},
                "default"
            );

            if (res.data.error_code === "NO_ERROR") {
                toast.success(<ToastAlertView message={t("message.saved")} />);
                resetForm();
                setIsOpen(false);
                onRefresh();
            }
        } catch (err) {
            console.log(err);
            toast.error(<ToastAlertView message={t("message.failed")} />);
        }
    };

    const handleClose = () => {
        resetForm();
        setIsOpen(false);
    };

    // Effects
    useEffect(() => {
        if (isOpen) {
            resetForm();
            // Set emailType from props
            updateRequestBody("emailType", emailType);
        }
    }, [isOpen, emailType]);

    useEffect(() => {
        validateEmailAddress();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestBody["emailAddress"]]);

    useEffect(() => {
        setIsValid(DataCompareService.checkIsAllTrue(isValidMap));
    }, [isValidMap]);

    const getModalTitle = () => {
        if (emailType === "ORDER") {
            return t("emailReceiver.addOrderEmail");
        } else {
            return t("emailReceiver.addDevelopmentEmail");
        }
    };

    return (
        <Modal style={modalStyle} isOpen={isOpen} onRequestClose={handleClose}>
            <div className="email-receiver-modal-content">
                <ModalCloseButton title={getModalTitle()} setIsOpen={setIsOpen} />

                <div style={{ marginTop: "30px" }}>
                    {/* Email Address */}
                    <div className="email-receiver-modal-field">
                        <label>{t("emailReceiver.emailAddress")}</label>
                        <input
                            className={`email-receiver-modal-input ${
                                !isValidMap["emailAddress"] && requestBody["emailAddress"] !== ""
                                    ? "invalid"
                                    : ""
                            }`}
                            type="email"
                            value={requestBody["emailAddress"]}
                            onChange={handleEmailAddressChange}
                            placeholder={t("emailReceiver.emailAddressPlaceholder")}
                        />
                        {!isValidMap["emailAddress"] && requestBody["emailAddress"] !== "" && (
                            <div className="email-receiver-error-text">
                                {t("emailReceiver.invalidEmail")}
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="email-receiver-modal-actions">
                        <button className="email-receiver-cancel-btn" onClick={handleClose}>
                            {t("button.cancel")}
                        </button>
                        <button
                            className="email-receiver-save-btn"
                            onClick={handleSubmit}
                            disabled={!isValid}
                        >
                            {t("button.save")}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AddEmailReceiverModal;
